// Rejected goods step 2 (2026-10-25; approved mockup AUMPidbNn2tJqJ4ujXtcPv, "Returns & credits" 7): one Ship action everywhere.
// Walid usually drives the goods back, so "In person" needs only the date and who received them; a courier adds its name and the
// tracking number. Saved by returns-credits.service shipReturn (Pending → Shipped).
import React, { useEffect, useState } from 'react';
import { DatePicker, Input, Modal, Radio, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { shipReturn } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';

export interface ShipTarget { returnId: number; code: string | null; qty: number; unit: string | null; item: string; vendor: string | null }

const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5f6672', minWidth: 0 };
const req = <span style={{ color: '#cf1322' }}>* </span>;

const ShipWindow: React.FC<{ target: ShipTarget | null; onClose: () => void; onSaved: () => void }> = ({ target, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const [method, setMethod] = useState<'in_person' | 'courier' | 'collected'>('in_person');
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [receivedBy, setReceivedBy] = useState('');
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!target) return;
    setMethod('in_person'); setDate(dayjs()); setReceivedBy(''); setCarrier(''); setTracking(''); setNote('');
  }, [target]);

  const ok = !!date && (method !== 'courier' || !!carrier.trim());
  const save = async () => {
    if (!target || !date) return;
    setSaving(true);
    try {
      await shipReturn(target.returnId, {
        method, date: date.format('YYYY-MM-DD'), receivedBy: receivedBy.trim() || undefined,
        carrier: method === 'courier' ? carrier.trim() : undefined, tracking: method === 'courier' ? tracking.trim() || undefined : undefined,
        note: note.trim() || undefined,
      });
      message.success(`${target.code || 'Return'} marked as shipped`);
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not mark it shipped');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!target}
      title={target ? <div style={{ paddingInlineEnd: 28 }}>Ship {target.code} · {target.qty}{target.unit ? ` ${target.unit}` : ''} <bdi>{target.item}</bdi> · <bdi>{target.vendor || ''}</bdi></div> : null}
      onCancel={onClose}
      onOk={save}
      okText="Mark shipped"
      okButtonProps={{ disabled: !ok, loading: saving }}
      maskClosable={false}
      width={compact ? 520 : 600}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div id="ship-how" style={{ fontWeight: 600, marginBottom: 6 }}>{req}How</div>
          <Radio.Group
            aria-labelledby="ship-how" optionType="button" value={method} onChange={(e) => setMethod(e.target.value)}
            options={[{ value: 'in_person', label: 'In person' }, { value: 'courier', label: 'Courier' }, { value: 'collected', label: 'The vendor collected it' }]}
            style={compact ? { display: 'flex', flexWrap: 'wrap', rowGap: 6 } : undefined}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <label style={lab}><span>{req}Date</span>
            <DatePicker value={date} onChange={setDate} format="D MMM YYYY" disabledDate={(d) => d.isAfter(dayjs(), 'day')} inputReadOnly={compact} style={{ width: '100%' }} />
          </label>
          <label style={lab}><span>Received by (at the vendor)</span>
            <Input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="e.g. Mr. Hany, the shop" maxLength={150} />
          </label>
          {method === 'courier' && (
            <>
              <label style={lab}><span>{req}Courier</span>
                <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. Bosta" maxLength={100} status={carrier.trim() ? undefined : 'error'} />
              </label>
              <label style={lab}><span>Tracking number</span>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Optional" maxLength={100} />
              </label>
            </>
          )}
          <label style={{ ...lab, gridColumn: '1 / -1' }}><span>Note</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" maxLength={500} />
          </label>
        </div>
      </div>
    </Modal>
  );
};

export default ShipWindow;
